import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	createHubItemGroup,
	CreateHubItemGroupModel,
	getCreateHubItemGroup,
	getUpdateHubItemGroup,
	HubItemGroupModel,
	HubItemGroupImageRestrictions,
	HubItemGroupSaveModel,
	KnowledgeItemGroupInfoSaveModel,
	updateHubItemGroup,
	UpdateHubItemGroupModel
} from "client/Api/Hub/hubSidebarManagement";
import {
	Dialog,
	DimensionRecommendationKind,
	Form,
	FormattedText,
	FormItem,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useStableImperativeCallback,
	useUnmountedCancellation
} from "client/Hooks";
import { EMPTY_ARRAY, getFileMetadata } from "client/Misc/utils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { ImageSelect } from "../ImageSelect/ImageSelect";
import { Base64FileUriUploadWithMetadataModel } from "client/Misc/commonTypes";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IRootValidation
} from "client/Validation";
import { getKnowledgeItemGroupImageUrl } from "../common";
import {
	doesNotExceedLength,
	hasMaxSize,
	isNotEmpty,
	isSupportedFileType
} from "client/Validation/rules";
import { IValidationConfiguration } from "client/Validation/builder";
import { tryParseServerErrors } from "client/Validation/serverErrors";
import { ModelPath } from "client/Validation/path";
import { InvalidInputValidationResult } from "client/Validation/input";
import { DotPathWildcardPattern } from "client/Misc/dotPathWildcardPattern";

export type EditMode =
	| {
		kind: "update";
		id: number;
	}
	| {
		kind: "new";
		itemKind: HubItemKind;
	};

export type EditHubItemGroupSaveResult = {
	id: number;
	name: string;
};

export type EditHubItemGroupDialogProps = {
	mode: EditMode;
	onClose: () => void;
	onSaved: (result: EditHubItemGroupSaveResult) => void;
};

export const EditHubItemGroupDialog = (props: EditHubItemGroupDialogProps): ReactElement => {
	const {
		mode,
		onClose: onCloseFromProps,
		onSaved
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(true);
	const [formData, setFormData] = useState<FormData>(defaultFormData);
	const [initialName, setInitialName] = useState<string>("");
	const [imageRestrictions, setImageRestrictions] = useState<HubItemGroupImageRestrictions>(defaultImageRestrictions);
	const [existingImageId, setExistingImageId] = useState<string | null>(null);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const onClose: () => void = useStableImperativeCallback(onCloseFromProps);

	const validationConfiguration = useMemo(
		(): IValidationConfiguration<FormData> => {
			const result: IValidationConfiguration<FormData> = staticValidationConfiguration.clone();

			const {
				maxImageSize,
				supportedImageTypes
			} = imageRestrictions;

			result
				.forObject("knowledgeItemInformation")
				.whenType((x) => x.isSet)
				.forObject("value")
				.forObject("image")
				.add(
					hasMaxSize(maxImageSize),
					isSupportedFileType(supportedImageTypes)
				);

			return result;
		},
		[imageRestrictions]
	);


	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	useEffect(
		(): (() => void) | void => {
			const load = async (mode: EditMode, signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					switch (mode.kind) {
						case "new":
							{
								const model: CreateHubItemGroupModel = await getCreateHubItemGroup(signal);
								setImageRestrictions(model.imageRestrictions);
								setInitialName("");
								setExistingImageId(null);
								setFormData(
									mode.itemKind === HubItemKind.KnowledgeItem
										? defaultKnowledgeItemFormData
										: defaultFormData
								);
								break;
							}
						case "update":
							{
								const model: UpdateHubItemGroupModel = await getUpdateHubItemGroup(mode.id, signal);
								setImageRestrictions(model.imageRestrictions);
								setInitialName(model.group.name);
								const { formData, imageId } = convertToFormData(model.group);
								setFormData(formData);
								setExistingImageId(imageId);
								break;
							}
						default:
							never(mode);
					}

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			};

			const cancellation = new AbortController();
			load(mode, cancellation.signal);
			return (): void => cancellation.abort();
		},
		[
			alertManager,
			mode,
			onClose
		]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);
			const validation: IRootValidation<HubItemGroupSaveModel> = formHandler.validation;
			try {
				const validationResult: InputValidationResult = await validation.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				let id: number;

				switch (mode.kind) {
					case "new":
						id = await createHubItemGroup({
							itemKind: mode.itemKind,
							...formData
						});
						break;
					case "update":
						await updateHubItemGroup(mode.id, formData);
						id = mode.id;
						break;
					default:
						never(mode);
				}

				onSaved({ id, name: formData.name });
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				const errorParseResult: TryResult<ReadonlyMap<ModelPath, InvalidInputValidationResult>> = tryParseServerErrors(
					e,
					[
						{
							pattern: new DotPathWildcardPattern("KnowledgeItemInformation.Value.Image.**"),
							replacement: "KnowledgeItemInformation.Value.Image"
						}
					]
				);

				if (errorParseResult.wasSuccessful) {
					const { result: errors } = errorParseResult;
					formHandler.validation
						.setServerErrors(errors);

					const imageError: string | undefined = errors
						.get(
							ModelPath.root
								.for("knowledgeItemInformation")
								.for("value")
								.for("image")
						)
						?.errorMessage;

					if (imageError) {
						alertManager.showError(imageError);
					}
				} else {
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy,
			mode,
			onSaved
		]
	);

	return (
		<Dialog
			trackingId="edit-hub-item-group-dialog"
			variant="left-slider"
			onClose={onClose}
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={
					mode.kind === "new"
						? localize("_NewGroup")
						: (
							<FormattedText
								text={localize("_EditNameGroup")}
								thingsToReplace={[
									{
										what: "{{Name}}",
										filler: initialName
									}
								]}
							/>
						)
				}
				onClose={onClose}
			/>

			<Dialog.Body>
				<Form onSubmit={saveHandler} hasWidthConstrained>
					<FormItem
						label={localize("_Name")}
						isRequired
					>
						<TextInput {...formHandler.for("name").attachToTextInput()} />
						<ValidationError {...formHandler.for("name").attachToValidationError()} />
					</FormItem>

					{formHandler.for("knowledgeItemInformation")
						.whenType(
							(x) => x.isSet,
							(x) => (
								<KnowledgeItemSection
									attachable={x.for("value")}
									imageId={existingImageId}
									imageRestrictions={imageRestrictions}
								/>
							)
						)
					}
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Save")}
						onClick={saveHandler}
						trackingId="save"
					/>

					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
EditHubItemGroupDialog.displayName = "EditHubItemGroupDialog";

type KnowledgeItemSectionProps = {
	attachable: IValueInputAttachable<KnowledgeItemGroupInfoSaveModel>;
	imageId: string | null;
	imageRestrictions: HubItemGroupImageRestrictions;
};

const KnowledgeItemSection = (props: KnowledgeItemSectionProps): ReactElement => {
	const {
		attachable,
		imageId,
		imageRestrictions: {
			supportedImageTypes,
			maxImageSize
		}
	} = props;

	const alertManager: AlertManager = useAlerts();

	const imageAttachable: IValueInputAttachable<OptionalModel<Base64FileUriUploadModel | null>> = attachable.for("image");
	const image: OptionalModel<Base64FileUriUploadModel | null> = imageAttachable.value;

	const imageSrc = useMemo(
		(): string | null => {
			if (image.isSet) {
				return image.value
					? image
						.value
						.uri
					: null;
			}

			if (imageId) {
				return getKnowledgeItemGroupImageUrl(imageId);
			}

			return null;
		},
		[image, imageId]
	);

	const imageChangeHandler = useCallback(
		(image: Base64FileUriUploadWithMetadataModel | null): void => {
			imageAttachable
				.reducer
				.setValue({
					isSet: true,
					value: image
				});

			imageAttachable
				.validation
				.clearServerErrors();

			imageAttachable
				.validation
				.triggerInputFinished();

			if (!image) {
				return;
			}

			const { size } = getFileMetadata(image);

			if (size > maxImageSize) {
				alertManager.showError(
					<FormattedText
						text={localize("_MaxSizeIs")}
						thingsToReplace={[
							{
								what: "{0}",
								filler: getDisplayFriendlySize(maxImageSize)
							}
						]}
					/>
				);
			}
		},
		[
			alertManager,
			maxImageSize,
			imageAttachable
		]
	);

	return (
		<>
			<FormItem label={localize("_Description")}>
				<TextInput {...attachable.for("description").attachToTextInput()} />
				<ValidationError {...attachable.for("description").attachToValidationError()} />
			</FormItem>

			<FormItem label={localize("_Image")}>
				<ImageSelect
					trackingId="select-image"
					src={imageSrc}
					onChange={imageChangeHandler}
					maxImageSize={maxImageSize}
					supportedFormats={supportedImageTypes}
					hasError={!imageAttachable.validation.isValid()}
					dimensionRecommendation={{
						kind: DimensionRecommendationKind.DimensionWithAspectRatio,
						height: 300,
						width: 600,
						aspectRatio: "2:1"
					}}
					supportedFormatsText={[
						localize("_FileExtensionJpg"),
						localize("_FileExtensionPng"),
						localize("_FileExtensionGif")
					]}
				/>

				<ValidationError {...attachable.for("image").attachToValidationError()} />
			</FormItem>
		</>
	);
};
KnowledgeItemSection.displayName = "KnowledgeItemSection";

type KnowledgeItemGroupInfoFormData = OverrideProperties<
	KnowledgeItemGroupInfoSaveModel,
	{
		image: OptionalModel<Base64FileUriUploadWithMetadataModel | null>;
	}
>;

type FormData = OverrideProperties<
	HubItemGroupSaveModel,
	{
		knowledgeItemInformation: OptionalModel<KnowledgeItemGroupInfoFormData>;
	}
>;

function convertToFormData(group: HubItemGroupModel): { formData: FormData; imageId: string | null; } {
	const {
		name,
		itemKind,
		knowledgeItemInformation: knowledgeInfo
	} = group;

	let imageId: string | null = null;
	let knowledgeItemInformation: OptionalModel<KnowledgeItemGroupInfoFormData> = { isSet: false };

	if (itemKind === HubItemKind.KnowledgeItem) {
		let description: string | null = null;

		if (knowledgeInfo.isSet) {
			({ description, imageId } = knowledgeInfo.value);
		}

		knowledgeItemInformation = {
			isSet: true,
			value: {
				description: description ?? "",
				image: {
					isSet: false
				}
			}
		};
	}

	const formData: FormData ={
		name,
		knowledgeItemInformation
	};

	return {
		formData,
		imageId
	};
};

const defaultFormData: FormData = {
	name: "",
	knowledgeItemInformation: { isSet: false }
};

const defaultKnowledgeItemFormData: FormData = {
	name: "",
	knowledgeItemInformation: {
		isSet: true,
		value: {
			description: "",
			image: { isSet: false }
		}
	}
};

const defaultImageRestrictions: HubItemGroupImageRestrictions = {
	maxImageSize: 0,
	supportedImageTypes: EMPTY_ARRAY
};

const staticValidationConfiguration = createValidationConfiguration<FormData>();

staticValidationConfiguration.forScalar("name")
	.add(
		isNotEmpty(),
		doesNotExceedLength(255)
	);

staticValidationConfiguration
	.forObject("knowledgeItemInformation")
	.whenType((x) => x.isSet)
	.forObject("value")
	.forScalar("description")
	.add(
		doesNotExceedLength(255)
	);

export function useEditHubItemGroupDialog(): [
	element: ReactElement | null,
	open: (props: Omit<EditHubItemGroupDialogProps, "onClose" | "onSaved">) => Promise<TryResult<EditHubItemGroupSaveResult>>
] {
	return useDataInputDialog(
		(props, onClose, onSave) => (
			<EditHubItemGroupDialog
				{...props}
				onClose={onClose}
				onSaved={onSave}
			/>
		)
	);
}
